import API_BASE_URL from '../config';
import type { RangeReplacePlan } from '../db/cacheStore';
import { reactionTable } from '../db/entityCache';
import * as hazardGateway from '../db/hazardGateway';
import { compareFeedOrder, idKey, toEpochMs } from '../db/time';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { patchFeedResolved } from './hazardResolvedStamp';
import userProfileService from './userProfileService';

/**
 * Bounded community feed window.
 *
 * One in-flight `getIncidentsMetadata?limit=150&offset=0` (ETag/304).
 * `prefix` is debounced; `full` runs immediately and cancels a pending prefix.
 * A request that arrives while a fetch is in flight is folded into one follow-up.
 */

export const FEED_WINDOW_LIMIT = 150;
export const EVICT_VIEWED_KEEP = 100;
export const EVICT_OWN_KEEP = 100;
const PREFIX_DEBOUNCE_MS = 300;
const ETAG_KEY = `etag:getIncidentsMetadata:${FEED_WINDOW_LIMIT}:0`;

export type FeedSyncMode = 'full' | 'prefix';

export interface FeedCachedRow {
  id: string;
  sortTs: number;
  idKey: string;
  viewedAt?: number;
  user_id?: string;
}

interface PlannedIncoming {
  id: string;
  sortTs: number;
  idKey: string;
  userId?: string;
  row: Record<string, unknown>;
}

type Waiter = {
  resolve: () => void;
  reject: (error: unknown) => void;
};

let running = false;
let scheduled: FeedSyncMode | null = null;
let waiters: Waiter[] = [];
let prefixTimer: ReturnType<typeof setTimeout> | null = null;
let prefixWaiters: Waiter[] = [];

function preferMode(current: FeedSyncMode | null, next: FeedSyncMode): FeedSyncMode {
  if (current === 'full' || next === 'full') {
    return 'full';
  }
  return 'prefix';
}

function kick(mode: FeedSyncMode): Promise<void> {
  scheduled = preferMode(scheduled, mode);
  return new Promise((resolve, reject) => {
    waiters.push({ resolve, reject });
    void pump();
  });
}

async function pump(): Promise<void> {
  if (running) {
    return;
  }
  running = true;
  try {
    while (scheduled) {
      scheduled = null;
      const batch = waiters.splice(0, waiters.length);
      try {
        await executeWindow();
        batch.forEach((waiter) => waiter.resolve());
      } catch (error) {
        batch.forEach((waiter) => waiter.reject(error));
      }
    }
  } finally {
    running = false;
    if (scheduled || waiters.length > 0) {
      void pump();
    }
  }
}

export function request(mode: FeedSyncMode): Promise<void> {
  if (mode === 'full') {
    if (prefixTimer !== null) {
      clearTimeout(prefixTimer);
      prefixTimer = null;
    }
    const pending = prefixWaiters.splice(0, prefixWaiters.length);
    const promise = kick('full');
    pending.forEach((waiter) => {
      promise.then(waiter.resolve, waiter.reject);
    });
    return promise;
  }
  return new Promise((resolve, reject) => {
    prefixWaiters.push({ resolve, reject });
    if (prefixTimer !== null) {
      clearTimeout(prefixTimer);
    }
    prefixTimer = setTimeout(() => {
      prefixTimer = null;
      const pending = prefixWaiters.splice(0, prefixWaiters.length);
      kick('prefix').then(
        () => pending.forEach((waiter) => waiter.resolve()),
        (error) => pending.forEach((waiter) => waiter.reject(error))
      );
    }, PREFIX_DEBOUNCE_MS);
  });
}

function asIncoming(row: Record<string, unknown>): PlannedIncoming | null {
  const id = typeof row.id === 'string' ? row.id : '';
  const sortTs = toEpochMs(row.utc_timestamp);
  if (!id || sortTs == null) {
    return null;
  }
  const userId = typeof row.user_id === 'string' ? row.user_id : undefined;
  return { id, sortTs, idKey: idKey(id), userId, row };
}

/**
 * Reconcile one metadata page into the cache plan.
 * Full page: rows missing above the oldest returned key are deletes; older
 * cached rows are demoted (`inWindow=0`) and then evicted unless they are
 * among the 100 most recently viewed or the user's 100 newest.
 * Short page: membership replace — cached rows absent from the response are deletes.
 */
export function planRangeReplace(
  cached: FeedCachedRow[],
  incoming: Array<Record<string, unknown>>,
  limit: number,
  userId: string
): RangeReplacePlan {
  const planned = incoming
    .map((row) => asIncoming(row))
    .filter((row): row is PlannedIncoming => row !== null);
  planned.sort((a, b) => compareFeedOrder(a, b));

  const incomingIds = new Set(planned.map((row) => row.id));
  const cachedById = new Map(cached.map((row) => [row.id, row]));
  const shortPage = planned.length < limit;
  const bound = planned.length > 0 ? planned[planned.length - 1] : null;

  const removeIds: string[] = [];
  const demote: FeedCachedRow[] = [];
  for (const row of cached) {
    if (incomingIds.has(row.id)) {
      continue;
    }
    const aboveBound =
      !shortPage &&
      bound != null &&
      compareFeedOrder(row, bound) < 0;
    if (shortPage || bound == null || aboveBound) {
      removeIds.push(row.id);
    } else {
      demote.push(row);
    }
  }

  const remain: FeedCachedRow[] = [];
  for (const row of planned) {
    const previous = cachedById.get(row.id);
    remain.push({
      id: row.id,
      sortTs: row.sortTs,
      idKey: row.idKey,
      viewedAt: previous?.viewedAt,
      user_id: row.userId ?? previous?.user_id,
    });
  }
  remain.push(...demote);

  const viewed = remain
    .filter((row) => typeof row.viewedAt === 'number')
    .sort((a, b) => {
      const delta = (b.viewedAt ?? 0) - (a.viewedAt ?? 0);
      if (delta !== 0) {
        return delta;
      }
      return compareFeedOrder(a, b);
    });
  const keepViewed = new Set(viewed.slice(0, EVICT_VIEWED_KEEP).map((row) => row.id));

  const own = remain
    .filter((row) => row.user_id === userId)
    .sort((a, b) => compareFeedOrder(a, b));
  const keepOwn = new Set(own.slice(0, EVICT_OWN_KEEP).map((row) => row.id));

  const demoteIds: string[] = [];
  for (const row of demote) {
    if (keepViewed.has(row.id) || keepOwn.has(row.id)) {
      demoteIds.push(row.id);
    } else {
      removeIds.push(row.id);
    }
  }

  return {
    upserts: planned.map((row) => ({ ...row.row, id: row.id, inWindow: 1 })),
    removeIds,
    demoteIds,
  };
}

async function executeWindow(): Promise<void> {
  const startedAt = Date.now();
  if (!hazardGateway.hasOpenStore()) {
    return;
  }
  const stored = await hazardGateway.getSyncMeta(ETAG_KEY);
  const headers: Record<string, string> = {};
  if (stored) {
    headers['If-None-Match'] = stored;
  }
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/getIncidentsMetadata?limit=${FEED_WINDOW_LIMIT}&offset=0`,
    { headers }
  );
  if (response.status === 304) {
    return;
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch hazards metadata: ${response.statusText}`);
  }
  const etag = response.headers.get('ETag');
  if (etag) {
    await hazardGateway.setSyncMeta(ETAG_KEY, etag);
  }
  const body: unknown = await response.json();
  if (!Array.isArray(body)) {
    throw new Error('getIncidentsMetadata returned a non-array body');
  }
  const rows = body.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object');
  patchFeedResolved(rows, startedAt);
  for (const row of rows) {
    const userId = typeof row.user_id === 'string' ? row.user_id : '';
    const username = typeof row.username === 'string' ? row.username : '';
    if (userId) {
      userProfileService.rememberUsername(userId, username);
    }
  }
  const cached = await hazardGateway.cachedFeedRows();
  const plan = planRangeReplace(cached, rows, FEED_WINDOW_LIMIT, hazardGateway.sessionUserId());
  await hazardGateway.applyFeedPlan(plan);
  const reactions = reactionRows(rows);
  if (reactions.length > 0) {
    await hazardGateway.applyFeedReactions(reactions);
  }
  await dropStaleCardReactions(rows);
}

async function dropStaleCardReactions(hazards: Array<Record<string, unknown>>): Promise<void> {
  for (const hazard of hazards) {
    const hazardId = typeof hazard.id === 'string' ? hazard.id : '';
    if (!hazardId) {
      continue;
    }
    const mine = new Set<string>();
    if (Array.isArray(hazard.reactions)) {
      for (const reaction of hazard.reactions) {
        if (!reaction || typeof reaction !== 'object') {
          continue;
        }
        const id = (reaction as { id?: unknown }).id;
        if (typeof id === 'string' && id.length > 0) {
          mine.add(id);
        }
      }
    }
    const rows = await reactionTable
      .filter(row => row.content_type === 'card' && row.content_id === hazardId)
      .toArray();
    for (const row of rows) {
      if (row.local_only || mine.has(row.id)) {
        continue;
      }
      await reactionTable.delete(row.id);
    }
  }
}

function reactionRows(hazards: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
  const out: Array<Record<string, unknown>> = [];
  for (const hazard of hazards) {
    const reactions = hazard.reactions;
    if (!Array.isArray(reactions)) {
      continue;
    }
    for (const reaction of reactions) {
      if (!reaction || typeof reaction !== 'object') {
        continue;
      }
      const row = reaction as Record<string, unknown>;
      const contentId = typeof row.content_id === 'string' ? row.content_id : hazard.id;
      out.push({ ...row, content_type: row.content_type ?? 'card', content_id: contentId });
    }
  }
  return out;
}
